import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  BookOpen, Star, Search, Loader2, Users, Clock, Languages, DollarSign,
  MessageSquare, CheckCircle2, UserPlus, Calendar, ArrowRight, Video, Brain, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { shareTutorProfile } from '../utils/whatsapp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TutorsPage = () => {
  const [tab, setTab] = useState('browse'); // browse | register | sessions | match
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sessions, setSessions] = useState([]);
  const [showBook, setShowBook] = useState(null); // tutor to book
  const [bookForm, setBookForm] = useState({ subject: '', date: '', time_slot: '', message: '' });
  const [booking, setBooking] = useState(false);
  const [showVideo, setShowVideo] = useState(null); // video room data
  // AI Match state
  const [matchSubject, setMatchSubject] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  // Register form
  const [regForm, setRegForm] = useState({ subjects: '', bio: '', hourly_rate: 20, languages: 'English' });
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = search ? `?subject=${encodeURIComponent(search)}` : '';
        const res = await axios.get(`${API}/tutors/browse${params}`);
        setTutors(res.data.tutors || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetch();
  }, [search]);

  const fetchSessions = async () => {
    try {
      const [student, tutor] = await Promise.all([
        axios.get(`${API}/tutors/my-sessions?role=student`),
        axios.get(`${API}/tutors/my-sessions?role=tutor`),
      ]);
      setSessions([
        ...(student.data.sessions || []).map(s => ({ ...s, _role: 'student' })),
        ...(tutor.data.sessions || []).map(s => ({ ...s, _role: 'tutor' })),
      ]);
    } catch (err) { console.error(err); }
  };

  const handleRegister = async () => {
    if (!regForm.subjects.trim()) return toast.error('Enter your subjects');
    setRegistering(true);
    try {
      await axios.post(`${API}/tutors/register`, {
        subjects: regForm.subjects.split(',').map(s => s.trim()),
        bio: regForm.bio,
        hourly_rate: Number(regForm.hourly_rate),
        languages: regForm.languages.split(',').map(s => s.trim()),
      });
      toast.success('Registered as tutor!');
      setTab('browse');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setRegistering(false);
  };

  const handleBook = async () => {
    if (!bookForm.date || !bookForm.time_slot) return toast.error('Pick date and time');
    setBooking(true);
    try {
      await axios.post(`${API}/tutors/book`, {
        tutor_id: showBook.user_id || showBook.tutor_id,
        subject: bookForm.subject || showBook.subjects?.[0],
        date: bookForm.date,
        time_slot: bookForm.time_slot,
        message: bookForm.message,
      });
      toast.success('Session booked! Tutor will confirm.');
      setShowBook(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBooking(false);
  };

  const handleAIMatch = async () => {
    if (!matchSubject.trim()) return toast.error('Enter a subject');
    setMatchLoading(true);
    try {
      const res = await axios.post(`${API}/tutor-match/find`, { subject: matchSubject });
      setMatches(res.data.matches || []);
      if (res.data.count === 0) toast.info('No matches found. Try a different subject.');
    } catch (err) { toast.error('Matching failed'); }
    setMatchLoading(false);
  };

  const handleStartVideoCall = async (session) => {
    try {
      const res = await axios.post(`${API}/video-tutoring/create-room`, {
        session_id: session.id,
        topic: `${session.subject} Tutoring`,
      });
      setShowVideo(res.data);
    } catch (err) { toast.error('Failed to start video call'); }
  };

  return (
    <div className="f500-page max-w-4xl mx-auto space-y-6" data-testid="tutors-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          Tutor Connect
        </h1>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{ id: 'browse', label: 'Find Tutors' }, { id: 'match', label: 'AI Match' }, { id: 'register', label: 'Become a Tutor' }, { id: 'sessions', label: 'My Sessions' }].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'sessions') fetchSessions(); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'}`}
            data-testid={`tutor-tab-${t.id}`}>
            {t.id === 'match' && <Sparkles className="w-3 h-3 inline mr-1" />}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'browse' && (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--isp-muted)]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by subject..." className="pl-9 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]" data-testid="tutor-search" />
          </div>
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tutors-grid">
              {tutors.length === 0 ? (
                <Card className="col-span-2 p-10 text-center  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
                  <Users className="w-10 h-10 mx-auto mb-3 text-[var(--isp-muted)]" />
                  <p className="text-[var(--isp-body)]">{search ? 'No tutors found for this subject' : 'No tutors yet. Be the first!'}</p>
                </Card>
              ) : tutors.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className=" bg-[var(--isp-glass)] border-[var(--isp-glass-border)] p-4" data-testid={`tutor-${t.id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {(t.name || 'T')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--isp-heading)]">{t.name}</p>
                        <p className="text-[10px] text-[var(--isp-muted)]">{t.institution || 'iStudyPro Tutor'} · {t.xp} XP</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-blue-400">GHS {t.hourly_rate}/hr</p>
                        {t.rating > 0 && <p className="text-[10px] text-yellow-400 flex items-center gap-0.5 justify-end"><Star className="w-3 h-3" /> {t.rating} ({t.rating_count})</p>}
                      </div>
                    </div>
                    <p className="text-xs text-[var(--isp-muted)] mb-2 line-clamp-2">{t.bio}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(t.subjects || []).map((s, si) => (
                        <span key={`tutor-${t.id || t.user_id || 'x'}-subj-${si}-${s}`} className="px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-300 text-[10px]">{s}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-[var(--isp-muted)] mb-3">
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {t.sessions_completed} sessions</span>
                      <span className="flex items-center gap-1"><Languages className="w-3 h-3" /> {(t.languages || []).join(', ')}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => { setShowBook(t); setBookForm({ subject: t.subjects?.[0] || '', date: '', time_slot: '', message: '' }); }}
                        className="flex-1 rounded-lg text-xs gap-1 bg-gradient-to-r from-blue-500 to-cyan-500" data-testid={`book-${t.id}`}>
                        <Calendar className="w-3 h-3" /> Book Session
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => shareTutorProfile(t.name, t.subjects || [])}
                        className="rounded-lg text-xs border-[var(--isp-glass-border)] text-green-300" data-testid={`share-tutor-${t.id}`}>
                        <MessageSquare className="w-3 h-3" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'register' && (
        <Card className="max-w-md mx-auto p-6  bg-[var(--isp-glass)] border-[var(--isp-glass-border)] space-y-4" data-testid="tutor-register">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-400" /> Register as Tutor</h2>
          <Input value={regForm.subjects} onChange={e => setRegForm(p => ({ ...p, subjects: e.target.value }))} placeholder="Subjects (comma separated)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="reg-subjects" />
          <Textarea value={regForm.bio} onChange={e => setRegForm(p => ({ ...p, bio: e.target.value }))} placeholder="About you as a tutor..." className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)] min-h-[80px]" data-testid="reg-bio" />
          <Input type="number" value={regForm.hourly_rate} onChange={e => setRegForm(p => ({ ...p, hourly_rate: e.target.value }))} placeholder="Hourly rate (GHS)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="reg-rate" />
          <Input value={regForm.languages} onChange={e => setRegForm(p => ({ ...p, languages: e.target.value }))} placeholder="Languages (comma separated)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="reg-langs" />
          <Button onClick={handleRegister} disabled={registering} className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500" data-testid="reg-submit">
            {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Tutoring'}
          </Button>
        </Card>
      )}

      {tab === 'sessions' && (
        <div className="space-y-3" data-testid="tutor-sessions">
          {sessions.length === 0 ? <p className="text-sm text-[var(--isp-muted)] text-center py-8">No sessions yet.</p> : sessions.map(s => (
            <Card key={s.id} className="p-4 rounded-xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${s.status === 'completed' ? 'bg-green-400' : s.status === 'confirmed' ? 'bg-blue-400' : 'bg-yellow-400'}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--isp-heading)]">{s.subject} — {s._role === 'tutor' ? `with ${s.student_name}` : `with ${s.tutor_name}`}</p>
                  <p className="text-[10px] text-[var(--isp-muted)]">{s.date} · {s.time_slot} · {s.status}</p>
                </div>
                <div className="flex items-center gap-2">
                  {s.status === 'confirmed' && (
                    <Button size="sm" onClick={() => handleStartVideoCall(s)}
                      className="rounded-lg gap-1 text-xs bg-blue-500 hover:bg-blue-600 text-white" data-testid={`video-call-${s.id}`}>
                      <Video className="w-3 h-3" /> Video Call
                    </Button>
                  )}
                  <span className="text-xs font-bold text-blue-400">GHS {s.hourly_rate}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* AI Match Tab */}
      {tab === 'match' && (
        <div className="space-y-4" data-testid="ai-match-tab">
          <div className="isp-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-[var(--isp-accent)]" />
              <h2 className="text-lg font-semibold text-[var(--isp-heading)]">AI Tutor Matching</h2>
            </div>
            <p className="text-sm text-[var(--isp-body)] mb-4">Enter a subject and our AI will find the best tutor based on expertise, ratings, XP, and your learning needs.</p>
            <div className="flex gap-2">
              <Input value={matchSubject} onChange={e => setMatchSubject(e.target.value)} placeholder="e.g. Biology, Calculus, SQL..."
                className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl flex-1" data-testid="match-subject" />
              <Button onClick={handleAIMatch} disabled={matchLoading} className="isp-btn-primary rounded-full gap-2" data-testid="match-btn">
                {matchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Find
              </Button>
            </div>
          </div>
          {matches.length > 0 && (
            <div className="space-y-3">
              {matches.map((m, i) => (
                <motion.div key={m.tutor_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <div className="isp-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--isp-accent)] to-emerald-600 flex items-center justify-center text-black text-sm font-bold flex-shrink-0">
                        {(m.name || 'T')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-[var(--isp-heading)]">{m.name}</p>
                          <span className="px-2 py-0.5 rounded-full bg-[var(--isp-accent)]/15 text-[var(--isp-accent)] text-[10px] font-bold">{m.match_score}% match</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(m.match_reasons || []).map((r, ri) => (
                            <span key={`match-${m.id || m.user_id || 'x'}-r-${ri}`} className="text-[10px] text-[var(--isp-body)]">{ri > 0 && '·'} {r}</span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--isp-muted)]">
                          <span>GHS {m.hourly_rate}/hr</span>
                          {m.rating > 0 && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-yellow-400" /> {m.rating}</span>}
                          <span>{m.sessions_completed} sessions</span>
                          <span>{m.xp} XP</span>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => { setShowBook({ ...m, user_id: m.tutor_id, subjects: m.subjects }); setBookForm({ subject: matchSubject, date: '', time_slot: '', message: '' }); }}
                        className="rounded-lg text-xs gap-1 bg-gradient-to-r from-blue-500 to-cyan-500" data-testid={`book-match-${i}`}>
                        <Calendar className="w-3 h-3" /> Book
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={!!showBook} onOpenChange={() => setShowBook(null)}>
        <DialogContent className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-w-sm" data-testid="book-dialog">
          <DialogHeader><DialogTitle>Book Session with {showBook?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={bookForm.subject} onChange={e => setBookForm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject" className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="book-subject" />
            <Input type="date" value={bookForm.date} onChange={e => setBookForm(p => ({ ...p, date: e.target.value }))} className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="book-date" />
            <Input value={bookForm.time_slot} onChange={e => setBookForm(p => ({ ...p, time_slot: e.target.value }))} placeholder="Time slot (e.g. 10:00-11:00)" className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="book-time" />
            <Textarea value={bookForm.message} onChange={e => setBookForm(p => ({ ...p, message: e.target.value }))} placeholder="Message (optional)" className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="book-msg" />
            <p className="text-xs text-[var(--isp-muted)]">Rate: GHS {showBook?.hourly_rate}/hr</p>
            <Button onClick={handleBook} disabled={booking} className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500" data-testid="book-submit">
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Call Dialog */}
      <Dialog open={!!showVideo} onOpenChange={() => setShowVideo(null)}>
        <DialogContent className="sm:max-w-2xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]" data-testid="video-call-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-blue-400" /> Video Tutoring</DialogTitle></DialogHeader>
          {showVideo && (
            <div className="space-y-3">
              <iframe
                src={`https://${showVideo.jitsi_domain}/${showVideo.room_code}#config.prejoinPageEnabled=false`}
                allow="camera; microphone; display-capture"
                className="w-full aspect-video rounded-xl border border-[var(--isp-glass-border)]"
                title="Video Tutoring"
                data-testid="tutor-jitsi-iframe"
              />
              <p className="text-xs text-[var(--isp-muted)] text-center">Room: {showVideo.room_code}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TutorsPage;
